import 'twin.macro'

import CaretDown from 'assets/icons/caret-down-grey.svg?component'
import dayjs from 'dayjs'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import tw from 'twin.macro'
import { useOnClickOutside } from 'usehooks-ts'
import { genNumArr } from 'utils'

import Typography from '../Typography'

type DaypickerProps = {
  value?: number | null
  isDisabled?: boolean
  disabledDays?: (day: number) => boolean
  onChange?: (value: number) => void
}

const getLabelText = (day: number) => {
  const lang = localStorage.getItem('i18nextLng')
  if (lang === 'ru') {
    if (day === 3 || day === 23) return `Каждое ${day}-ье число месяца`
    return `Каждое ${day}-е число месяца`
  } else {
    if (day === 10) return `Айдың әр ${day}-шы күні`
    return `Айдың әр ${day}-шi күні`
  }
}

const DayPicker = ({ value = null, isDisabled, disabledDays, onChange }: DaypickerProps) => {
  // Refs
  const ref = useRef(null)
  const { t } = useTranslation()

  // State
  const [isOpen, setIsOpen] = useState(false)
  const [selectedDay, setSelectedDay] = useState<number | null>(value)

  // Computed value
  const daysInCurrentMonth = useMemo(() => dayjs().daysInMonth(), [])

  // Handlers
  const handleDayClick = (day: number) => {
    setSelectedDay(day)
    onChange?.(day)
    setIsOpen(false)
  }

  const handleClickOutside = () => {
    if (!isOpen) return
    setIsOpen(false)
  }

  // Effects
  useOnClickOutside(ref, handleClickOutside)

  return (
    <div ref={ref} tw='relative'>
      <label>
        <div
          css={[
            tw`py-[13px] px-5 rounded-s14 border border-transparent flex items-center bg-input min-w-min transition`,
            isOpen && tw`border-active`
          ]}
        >
          <Typography variant='subbody' weight='bold' twStyle={tw`select-none`}>
            {selectedDay ? getLabelText(selectedDay) : t('flow-choose-digit')}
          </Typography>
          {!isDisabled ? <CaretDown tw='flex-shrink-0 ml-auto' /> : null}
        </div>
        <input
          type='checkbox'
          tw='hidden'
          disabled={isDisabled}
          checked={isOpen}
          onChange={() => setIsOpen(prevState => !prevState)}
        />
      </label>
      {isOpen ? (
        <div tw='grid grid-cols-7 absolute z-10 mt-1 inset-x-0 bg-white dark:bg-grey-middle p-3 rounded-s14 border border-tertiary'>
          {genNumArr(daysInCurrentMonth).map(day => (
            <button
              className={`bvi-button-no-styles`}
              css={[
                tw`flex items-center justify-center w-10 h-10 rounded-full border border-transparent transition border-active 
                   disabled:text-grey-middle dark:disabled:text-grey-dark disabled:hover:border-transparent disabled:hover:cursor-not-allowed disabled:border-none`,
                selectedDay === day && tw`bg-primary-middle text-white`
              ]}
              disabled={disabledDays?.(day)}
              key={`${day}`}
              onClick={() => handleDayClick(day)}
            >
              <Typography as='span' variant='subbody' weight='bold'>
                {day}
              </Typography>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default DayPicker
